import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, Spinner } from 'flowbite-react';
import { supabase } from '@/lib/supabase';
import { HiMail, HiCheckCircle, HiClock } from 'react-icons/hi';

export default function WaitingApproval() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Obtener perfil del usuario
    const fetchUserProfile = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        setLoading(false);
        return;
      }

      if (!data) {
        console.error('User profile not found');
        setLoading(false);
        return;
      }

      setLoading(false);

      // Si el usuario ya está activo, redirigir al dashboard
      if (data.status === 'active') {
        navigate('/dashboard');
        return;
      }

      // Suscribirse a cambios en tiempo real
      const channel = supabase
        .channel('user-status-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'users',
            filter: `id=eq.${user.id}`,
          },
          (payload) => {
            const newStatus = payload.new.status;

            if (newStatus === 'active') {
              // Usuario fue aprobado, redirigir al dashboard
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    fetchUserProfile();

  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Spinner size="xl" className="text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4 py-12">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <div className="text-center p-6 space-y-6">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <HiClock className="w-10 h-10 text-white animate-pulse" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Esperando Aprobación
            </h1>
            <p className="text-base text-gray-600">
              Tu cuenta está pendiente de revisión
            </p>

            <div className="space-y-4 pt-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <HiMail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    Verifica tu email
                  </p>
                  <p className="text-sm text-blue-700">
                    Hemos enviado un email de confirmación a <strong>{user?.email}</strong>.
                    Por favor verifica tu correo electrónico.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <HiCheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900 mb-1">
                    Revisión pendiente
                  </p>
                  <p className="text-sm text-amber-700">
                    Un administrador revisará tu solicitud y te notificaremos cuando tu cuenta sea activada.
                    Esto puede tomar algunos minutos.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-center text-sm text-gray-600">
                Te redirigiremos automáticamente cuando tu cuenta sea aprobada.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
